<template>
  <el-dialog
    v-if="visible"
    title="キャンペーンの編集"
    :visible.sync="visible"
    :before-close="handleClose"
    custom-class="with-title"
  >
    <div class="instagram-campaign-edit">
      <CampaignCreateAccount />
    </div>
    <div class="instagram-campaign-edit">
      <CampaignCreateTitle />
    </div>
    <div class="instagram-campaign-edit">
      <CampaignCreatePeriod />
    </div>
    <div class="instagram-campaign-edit">
      <CampaignCreateInstagramCondition />
    </div>
    <div class="edit-button">
      <el-button @click="onDelete">
        <Icon name="delete-forever" space="right" />
        削除
      </el-button>
      <div>
        <el-button @click="handleClose">キャンセル</el-button>
        <el-button :disabled="isEditDisabled" type="primary" @click="onSave">保存</el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, getCurrentInstance, ref } from 'vue'

import Icon from '@/client/components/_atoms/Icon.vue'
import CampaignCreateAccount from '@/client/components/_organisms/CampaignCreateAccount.vue'
import CampaignCreateInstagramCondition from '@/client/components/_organisms/CampaignCreateInstagramCondition.vue'
import CampaignCreatePeriod from '@/client/components/_organisms/CampaignCreatePeriod.vue'
import CampaignCreateTitle from '@/client/components/_organisms/CampaignCreateTitle.vue'
import { useCampaignDialogStore } from '@/client/store/useCampaignDialogStore'

const visible = ref(false)

const campaignDialogStore = useCampaignDialogStore()
const { is_form_change, is_edit_disabled } = storeToRefs(campaignDialogStore)

// getters (computed)
const isFormChange = computed(() => is_form_change.value)
const isEditDisabled = computed(() => is_edit_disabled.value)

// actions
const setCampaignId = (id: number) => campaignDialogStore.setCampaignId(id)
const fetchCampaign = () => campaignDialogStore.fetchCampaign()
const fetchCampaignAccounts = () => campaignDialogStore.fetchCampaignAccounts()
const updateCampaign = () => campaignDialogStore.updateCampaign()
const deleteCampaign = () => campaignDialogStore.deleteCampaign()
const setMode = (mode: 'create' | 'edit' | 'copy') => campaignDialogStore.setMode(mode)
const reset = () => campaignDialogStore.reset()

// 通知処理がvue2のため、anyで補完
const { proxy } = getCurrentInstance() as any

// 親コンポーネントイベント通知
const emit = defineEmits<{ (e: 'campaignUpdated'): void }>()

/**
 * ダイアログを開く（Instagramキャンペーン編集用）
 */
async function open(campaignId: number) {
  await setCampaignId(campaignId)
  await setMode('edit')
  const response = await fetchCampaign()

  if (!response.data) {
    proxy.$notify({
      title: 'キャンペーンの取得に失敗しました。',
      message: '恐れ入りますが、時間をおいて再度お試しください。',
      customClass: 'danger'
    })

    visible.value = false
    await reset()
    return
  }

  await fetchCampaignAccounts()

  visible.value = true
}

/**
 * ダイアログキャンペーンを閉じる
 */
function handleClose() {
  if (!isFormChange.value) {
    visible.value = false
    reset()
    return
  }

  const confirm = window.confirm('編集内容が保存されていません。ダイアログを閉じますか？')

  if (!confirm) {
    return
  }

  visible.value = false
  reset()
}

/**
 * 保存ボタン
 */
async function onSave() {
  const result = await updateCampaign()

  if (result.error && result.error.type === 'MAX_CAMPAIGN_COUNT_OVER') {
    return proxy.$notify({
      title: 'キャンペーンの変更に失敗しました。',
      message: '同時開催可能件数を超えるため、指定の期間ではキャンペーンを開催できません。',
      customClass: 'danger'
    })
  }
  if (!result.data) {
    return proxy.$notify({
      title: 'キャンペーンの変更に失敗しました。',
      message: '恐れ入りますが、時間をおいて再度お試しください。',
      customClass: 'danger'
    })
  }

  proxy.$notify({
    title: 'キャンペーンを変更しました。',
    customClass: 'success'
  })

  // キャンペーン一覧の更新を親コンポーネントに通知
  emit('campaignUpdated')

  visible.value = false
  reset()
}

/**
 * 削除ボタン
 */
async function onDelete() {
  const confirm = window.confirm('キャンペーンを削除してよろしいですか？')

  if (!confirm) {
    return
  }

  const result = await deleteCampaign()

  if (result.error && result.error.type === 'NO_DATA_EXISTS') {
    return proxy.$notify({
      title: '対象のキャンペーンはすでに削除されています。',
      customClass: 'danger'
    })
  }

  if (!result.data) {
    return proxy.$notify({
      title: 'キャンペーンの削除に失敗しました。',
      customClass: 'danger'
    })
  }

  // キャンペーン一覧の更新を親コンポーネントに通知
  emit('campaignUpdated')

  proxy.$notify({
    title: 'キャンペーンを削除しました。',
    customClass: 'success'
  })

  visible.value = false
  reset()
}

defineExpose({ open, handleClose })
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.instagram-campaign-edit:not(:first-child) {
  margin-top: $spacing-unit * 2;
}

.edit-button {
  display: flex;
  justify-content: space-between;
  margin-top: $spacing-unit * 3;
}
</style>
